import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

/**
 * Hook pour vérifier si l'utilisateur connecté a le rôle admin
 * 
 * 🔐 Sécurité: Vérifie le rôle côté serveur via RPC
 * Jamais de vérification côté client uniquement!
 */
export const useAdminCheck = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    logger.debug('🚀 useAdminCheck - Hook initialisé');
    
    const checkAdminRole = async () => {
      logger.debug('🔍 useAdminCheck - Début de la vérification');
      
      try {
        // 1. Vérifier la session (plus stable que getUser)
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        logger.debug('👤 useAdminCheck - Session:', {
          userId: session?.user?.id,
          hasSession: !!session,
          error: sessionError
        });
        
        if (sessionError) {
          logger.error('❌ useAdminCheck - Erreur session:', sessionError);
          setIsAdmin(false);
          setLoading(false);
          return;
        }
        
        if (!session?.user) {
          logger.debug('⚠️ useAdminCheck - Pas de session active');
          setIsAdmin(false);
          setLoading(false);
          return;
        }

        // 2. Appel de la fonction has_role (vérification serveur)
        logger.debug('🔄 useAdminCheck - Appel RPC has_role pour:', session.user.id);
        
        const { data: hasRole, error: rpcError } = await supabase.rpc('has_role', {
          _user_id: session.user.id,
          _role: 'admin'
        } as { _user_id: string, _role: 'admin' });

        logger.debug('📊 useAdminCheck - Résultat RPC:', { 
          hasRole, 
          error: rpcError
        });

        if (rpcError) {
          logger.error('❌ useAdminCheck - Erreur RPC:', rpcError);
          setIsAdmin(false);
          setLoading(false);
        } else {
          const adminStatus = !!hasRole;
          logger.debug('✅ useAdminCheck - Statut admin:', adminStatus);
          setIsAdmin(adminStatus);
          setLoading(false);
        }
      } catch (error) {
        logger.error('💥 useAdminCheck - Erreur inattendue:', error);
        setIsAdmin(false);
        setLoading(false);
      }
    };

    checkAdminRole();

    // Écouter les changements d'authentification (seulement SIGNED_IN et SIGNED_OUT)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, _session) => {
      logger.debug('🔄 useAdminCheck - Auth event:', event);
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        checkAdminRole();
      }
    });

    return () => {
      logger.debug('🔚 useAdminCheck - Cleanup');
      subscription.unsubscribe();
    };
  }, []);

  return { isAdmin, loading };
};
